/**
 * /use-cases/analyst-investment-memo — analyst workflow for investment
 * memos with source → fact → claim → decision chains.
 */
import { MarketingShell } from '../MarketingShell';

export function AnalystInvestmentMemo() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/use-cases/analyst-investment-memo"
      documentTitle="Use case: Analyst investment memos · Draftlize"
      documentDescription="How analysts and investors build investment memos in Draftlize. Source → fact → claim → decision chains where every claim cites the underlying observation and decisions auto-flag stale when assumptions move."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Use case · Investment memos</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            Investment memos that<br/>
            <em>show their work.</em>
          </h1>
          <p className="lede">
            For analysts building investment cases. Source → fact → claim → decision chains where every claim cites its underlying observation, and decisions auto-flag stale when an assumption moves.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">The standard analyst stack: <em>where it leaks.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Sources buried in docs</h3>
            <p>You quote a 10-K figure in your memo. Three weeks later, partner asks "what was the source?" You search 47 PDFs. Maybe find it. Maybe not.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Claims drift from facts</h3>
            <p>You wrote "TAM $4B" in week 1 based on Q3 segment data. Q4 data revises TAM to $3.1B. Your memo still says $4B. Nobody noticed.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Conviction without provenance</h3>
            <p>You write "BUY PT $85." What chain of reasoning got there? In a standard memo it's prose; in three weeks you can't reconstruct which observations the price target rests on.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The Draftlize <em>analyst loop.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Sources as observations</h3>
            <p>Drop 10-K, earnings call transcript, sector report into <code>_refs/</code>. Agent reads them, creates <code>observation</code> cards for each cited figure with <code>→derived-from [[10-K-q3-2025.pdf]]</code> wikilinks. Every number traces back.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Facts and claims as cards</h3>
            <p><code>c-04 fact: gross margin 38% Q3 2025</code> citing 3 observations. <code>c-08 claim: GM expansion to 45% by FY27</code> with <code>→supports c-04, c-12, c-15</code>. Claim cards live alongside facts but are clearly different epistemic status.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Decisions with explicit chain</h3>
            <p><code>c-20 decision: BUY PT $85</code> with <code>→derived-from</code> a tree of claims, each supported by facts, each citing observations. The chain is queryable. Change a fact → claim flags stale → decision flags stale. Partner audit trail is automatic.</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Real workflow: <em>tracking a portfolio company over 6 months.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">M1</span>
            <h3 className="serif">Initial memo</h3>
            <p>~60 cards across observations / facts / claims / decisions. Original BUY PT card has a 4-level chain of provenance underneath it. Partner approves on Friday.</p>
          </div>
          <div className="problem-col">
            <span className="num">M3</span>
            <h3 className="serif">Q4 earnings drops</h3>
            <p>You update 3 observation cards (revenue, GM, churn). Stale propagation flags 12 dependent cards across the chain — including the BUY PT decision. You see exactly which assumptions moved and re-derive the conclusion deliberately.</p>
          </div>
          <div className="problem-col">
            <span className="num">M6</span>
            <h3 className="serif">Cross-portfolio conflict</h3>
            <p>Your memo says "competitive moat from network effects." Your colleague's memo on a competitor says "no real moat in the category." Cross-project conflict detection (when enabled) files a gap card in both. Partner sees both, decides which is right.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          The substrate forces you to be explicit about the chain. Every claim has cited facts. Every decision has derivable provenance. Partner reviews go from "trust me" to "click to inspect."
        </blockquote>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Try it on <em>one memo.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Your free $5 is enough to seed one full memo (~40-80 cards) and feel one earnings update re-trigger the chain. Most analysts notice the audit-trail value by day 3.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
